import { useState, useEffect, useMemo, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../../../services/api'
import { useAccount } from '../../../hooks/useAccount'
import { useSubscription } from '../../../hooks/useSubscription'
import { toast } from 'react-toastify'
import ThemeSelector from './ThemeSelector'
import ProductSelector from './ProductSelector'
import BioPagePreview from './BioPagePreview'
import BioPageIntro from './BioPageIntro'

function BioPageBuilder() {
  const { bioPageId: routeBioPageId } = useParams()
  const { t } = useTranslation('ecommerce')
  const { selectedAccount } = useAccount()
  const { plan, loading: subscriptionLoading } = useSubscription(selectedAccount?.id)
  const isFreePlan = plan === 'free' && !subscriptionLoading

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [theme, setTheme] = useState('light')
  const [colors, setColors] = useState({ primary: '#3b82f6', background: '#ffffff' })
  const [selectedProductIds, setSelectedProductIds] = useState([])
  const [logoUrl, setLogoUrl] = useState('')
  const [products, setProducts] = useState([])

  const [shopUrl, setShopUrl] = useState('')
  const [impressumUrl, setImpressumUrl] = useState('')
  const [datenschutzUrl, setDatenschutzUrl] = useState('')
  const [facebookPixelId, setFacebookPixelId] = useState('')
  const [ga4Id, setGa4Id] = useState('')
  const [isPublished, setIsPublished] = useState(false)
  const [autoAddStoryProducts, setAutoAddStoryProducts] = useState(true)

  // UI state
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [savedBioPageId, setSavedBioPageId] = useState(routeBioPageId)
  const [copied, setCopied] = useState(false)
  const [showIntro, setShowIntro] = useState(!routeBioPageId)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [brandProfile, setBrandProfile] = useState(null)
  const [brandProfileLoading, setBrandProfileLoading] = useState(false)

  // Auto-close upgrade modal if plan changes (e.g. upgraded in another tab)
  useEffect(() => {
    if (!isFreePlan) setShowUpgradeModal(false)
  }, [isFreePlan])

  // Fetch brand profile (always, for "Fill with AI" button)
  useEffect(() => {
    if (!selectedAccount?.id) return
    const fetchBrandProfile = async () => {
      setBrandProfileLoading(true)
      try {
        const profile = await api.getBrandProfile(selectedAccount.id)
        if (profile?.status === 'completed') {
          setBrandProfile(profile)
        }
      } catch {
        // No brand profile available
      } finally {
        setBrandProfileLoading(false)
      }
    }
    fetchBrandProfile()
  }, [selectedAccount?.id])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const productsResponse = await api.getProducts()
        setProducts(productsResponse.products || [])

        if (routeBioPageId) {
          // Existing Bio Page - load its data
          const page = await api.getBioPage(routeBioPageId)
          if (page) {
            setTitle(page.title || '')
            setDescription(page.description || '')
            setTheme(page.theme || 'light')
            setColors(page.colors || { primary: '#3b82f6', background: '#ffffff' })
            setSelectedProductIds(page.product_ids || [])
            setLogoUrl(page.logo_url || '')
            setShopUrl(page.shop_url || '')
            setImpressumUrl(page.impressum_url || '')
            setDatenschutzUrl(page.datenschutz_url || '')
            setFacebookPixelId(page.facebook_pixel_id || '')
            setGa4Id(page.ga4_id || '')
            setIsPublished(page.is_published || false)
            setAutoAddStoryProducts(page.auto_add_story_products ?? true)
          }
        }
      } catch (error) {
        console.error('Failed to fetch data:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [routeBioPageId])

  // Auto-prefill for new pages from brand profile
  useEffect(() => {
    if (routeBioPageId || !brandProfile) return
    if (brandProfile.bio_page_title && !title) {
      setTitle(brandProfile.bio_page_title)
    }
    if (brandProfile.bio_page_description && !description) {
      setDescription(brandProfile.bio_page_description)
    }
  }, [brandProfile]) // eslint-disable-line react-hooks/exhaustive-deps

  const fillFromAI = (field) => {
    const value = field === 'title' ? brandProfile?.bio_page_title : brandProfile?.bio_page_description
    if (!value) return
    const current = field === 'title' ? title : description
    const setter = field === 'title' ? setTitle : setDescription

    if (current && current !== value) {
      if (!window.confirm(t('bioPageBuilder.replaceConfirm', 'Replace current text with AI recommendation?'))) return
    }
    setter(value)
  }

  const selectedProducts = useMemo(() => {
    return products.filter((p) => selectedProductIds.includes(p.product_id))
  }, [products, selectedProductIds])

  const bioPageUrl = selectedAccount?.username
    ? `https://bio.storyplannerpro.de/${selectedAccount.username}`
    : null

  const handleSave = useCallback(async () => {
    setSaving(true)
    setSaved(false)

    try {
      const payload = {
        title,
        description,
        theme,
        colors,
        product_ids: selectedProductIds,
        logo_url: logoUrl,
        shop_url: shopUrl,
        impressum_url: impressumUrl,
        datenschutz_url: datenschutzUrl,
        facebook_pixel_id: facebookPixelId,
        ga4_id: ga4Id,
        auto_add_story_products: autoAddStoryProducts,
      }

      let currentBioPageId = savedBioPageId
      if (savedBioPageId) {
        await api.updateBioPage(savedBioPageId, payload)
      } else {
        const result = await api.createBioPage(payload)
        currentBioPageId = result.bio_page_id
        setSavedBioPageId(currentBioPageId)
      }

      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
      return currentBioPageId
    } catch (error) {
      console.error('Failed to save bio page:', error)
      toast.error(t('bioPage.saveError', 'Failed to save bio page. Please try again.'))
      return null
    } finally {
      setSaving(false)
    }
  }, [
    title,
    description,
    theme,
    colors,
    selectedProductIds,
    logoUrl,
    shopUrl,
    impressumUrl,
    datenschutzUrl,
    facebookPixelId,
    ga4Id,
    autoAddStoryProducts,
    savedBioPageId,
  ])

  const handleTogglePublish = useCallback(async () => {
    // Publishing requires Starter plan or higher
    if (!isPublished && isFreePlan) {
      setShowUpgradeModal(true)
      return
    }

    let bioPageIdToPublish = savedBioPageId

    if (!bioPageIdToPublish) {
      // Must save first - get the ID directly from save result
      bioPageIdToPublish = await handleSave()
      if (!bioPageIdToPublish) return
    }

    setPublishing(true)
    try {
      const result = await api.publishBioPage(bioPageIdToPublish, !isPublished)
      setIsPublished(result.is_published)
    } catch (error) {
      if (error?.response?.status === 403) {
        setShowUpgradeModal(true)
      } else {
        console.error('Failed to toggle publish:', error)
        toast.error(t('bioPage.publishError', 'Failed to update publish status. Please try again.'))
      }
    } finally {
      setPublishing(false)
    }
  }, [savedBioPageId, handleSave, isPublished, isFreePlan])

  const handleCopyUrl = useCallback(() => {
    if (bioPageUrl) {
      navigator.clipboard.writeText(bioPageUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }, [bioPageUrl])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <span className="text-gray-500">{t('common.loading', 'Loading...')}</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">
          {t('bioPageBuilder.title', 'Bio Page Builder')}
        </h1>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? t('bioPageBuilder.saving', 'Saving...') : t('bioPageBuilder.save', 'Save')}
        </button>
      </div>

      {saved && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-800">
            {t('bioPageBuilder.savedSuccess', 'Bio page saved successfully!')}
          </p>
        </div>
      )}

      {/* Intro Section - collapsible */}
      {showIntro && !routeBioPageId && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <button
            type="button"
            onClick={() => setShowIntro(!showIntro)}
            className="w-full p-4 flex items-center justify-between text-left bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <span className="font-medium text-gray-900">
              {t('bioPageIntro.headline')}
            </span>
            <svg
              className="w-5 h-5 text-gray-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="p-6">
            <BioPageIntro compact showFAQ />
          </div>
        </div>
      )}

      {/* Show help toggle when intro is hidden */}
      {!showIntro && !routeBioPageId && (
        <button
          type="button"
          onClick={() => setShowIntro(true)}
          className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {t('bioPageBuilder.showHelp', 'Was ist eine Bio Page?')}
        </button>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Settings Panel */}
        <div className="space-y-6">
          {/* Basic Info */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {t('bioPageBuilder.basicInfo', 'Basic Information')}
            </h2>

            <div>
              <label
                htmlFor="bio-title"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t('bioPageBuilder.titleLabel', 'Title')}
              </label>
              <input
                type="text"
                id="bio-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('bioPageBuilder.titlePlaceholder', 'Your shop name')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
              />
              {brandProfile ? (
                <button
                  type="button"
                  onClick={() => fillFromAI('title')}
                  disabled={brandProfileLoading}
                  className="text-sm text-primary-600 hover:text-primary-700 mt-1 inline-flex items-center gap-1 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  ✨ {t('bioPageBuilder.fillWithAI', 'Fill with AI recommendation')}
                </button>
              ) : !brandProfileLoading && (
                <span className="text-sm text-gray-400 mt-1 inline-flex items-center gap-1">
                  ✨ {t('bioPageBuilder.setupBrandProfile', 'Set up your brand profile for AI recommendations.')}{' '}
                  <Link to={`/accounts/${selectedAccount?.id}/brand-settings`} className="text-primary-600 hover:text-primary-700 underline">
                    {t('bioPageBuilder.setupBrandProfileLink', 'Brand Settings')}
                  </Link>
                </span>
              )}
            </div>

            <div>
              <label
                htmlFor="bio-description"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t('bioPageBuilder.descriptionLabel', 'Description')}
              </label>
              <textarea
                id="bio-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('bioPageBuilder.descriptionPlaceholder', 'A short description of your shop')}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
              />
              {brandProfile ? (
                <button
                  type="button"
                  onClick={() => fillFromAI('description')}
                  disabled={brandProfileLoading}
                  className="text-sm text-primary-600 hover:text-primary-700 mt-1 inline-flex items-center gap-1 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  ✨ {t('bioPageBuilder.fillWithAI', 'Fill with AI recommendation')}
                </button>
              ) : !brandProfileLoading && (
                <span className="text-sm text-gray-400 mt-1 inline-flex items-center gap-1">
                  ✨ {t('bioPageBuilder.setupBrandProfile', 'Set up your brand profile for AI recommendations.')}{' '}
                  <Link to={`/accounts/${selectedAccount?.id}/brand-settings`} className="text-primary-600 hover:text-primary-700 underline">
                    {t('bioPageBuilder.setupBrandProfileLink', 'Brand Settings')}
                  </Link>
                </span>
              )}
            </div>

            <div>
              <label
                htmlFor="bio-logo"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t('bioPageBuilder.logoLabel', 'Logo URL')}
              </label>
              <input
                type="url"
                id="bio-logo"
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                placeholder="https://..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                {t('bioPageBuilder.logoHelp', 'Direct link to your logo image (PNG or SVG). You\'ll find this in your shop settings or media library.')}
              </p>
            </div>
          </div>

          {/* Shop URL Section */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {t('bioPageBuilder.shopSection', 'Shop Link')}
            </h2>

            <div>
              <label
                htmlFor="shop-url"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t('bioPageBuilder.shopUrl', 'Shop URL')}
              </label>
              <input
                type="url"
                id="shop-url"
                value={shopUrl}
                onChange={(e) => setShopUrl(e.target.value)}
                placeholder={t('bioPageBuilder.shopUrlPlaceholder', 'https://your-shop.com')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                {t('bioPageBuilder.shopUrlHelp', 'Fallback link if a product has no direct URL')}
              </p>
            </div>
          </div>

          {/* Theme Selection */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {t('bioPageBuilder.theme.title', 'Theme')}
            </h2>
            <ThemeSelector
              value={theme}
              onChange={setTheme}
              colors={colors}
              onColorChange={setColors}
            />
          </div>

          {/* Product Selection */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {t('bioPageBuilder.products.title', 'Products')}
            </h2>

            {/* Auto-add story products toggle */}
            <div className="flex items-start gap-3">
              <button
                type="button"
                role="switch"
                aria-checked={autoAddStoryProducts}
                onClick={() => setAutoAddStoryProducts(!autoAddStoryProducts)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                  autoAddStoryProducts ? 'bg-primary-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    autoAddStoryProducts ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {t('bioPageBuilder.autoAdd.label', 'Automatically show products from published stories')}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {t('bioPageBuilder.autoAdd.hint', 'Products appear on your Bio Page while the story is live (24h)')}
                </p>
              </div>
            </div>

            {!autoAddStoryProducts && (
              <ProductSelector
                selectedIds={selectedProductIds}
                onChange={setSelectedProductIds}
                maxProducts={10}
              />
            )}
          </div>

          {/* Legal Section */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {t('bioPageBuilder.legal.title', 'Legal Information')}
            </h2>

            <div>
              <label
                htmlFor="impressum-url"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t('bioPageBuilder.legal.impressum', 'Impressum URL')}
              </label>
              <input
                type="url"
                id="impressum-url"
                value={impressumUrl}
                onChange={(e) => setImpressumUrl(e.target.value)}
                placeholder={t('bioPageBuilder.legal.impressumPlaceholder', 'https://your-shop.com/impressum')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label
                htmlFor="datenschutz-url"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t('bioPageBuilder.legal.datenschutz', 'Privacy Policy URL')}
              </label>
              <input
                type="url"
                id="datenschutz-url"
                value={datenschutzUrl}
                onChange={(e) => setDatenschutzUrl(e.target.value)}
                placeholder={t('bioPageBuilder.legal.datenschutzPlaceholder', 'https://your-shop.com/datenschutz')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Tracking Section */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {t('bioPageBuilder.tracking.title', 'Analytics & Tracking')}
            </h2>
            <p className="text-sm text-gray-500">
              {t('bioPageBuilder.tracking.description', 'Add your own tracking IDs to track visits and conversions on your bio page.')}
            </p>

            <div>
              <label
                htmlFor="fb-pixel"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t('bioPageBuilder.tracking.facebookPixel', 'Facebook Pixel ID')}
              </label>
              <input
                type="text"
                id="fb-pixel"
                value={facebookPixelId}
                onChange={(e) => setFacebookPixelId(e.target.value)}
                placeholder={t('bioPageBuilder.tracking.facebookPixelPlaceholder', 'e.g., 1234567890123456')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label
                htmlFor="ga4-id"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t('bioPageBuilder.tracking.ga4', 'Google Analytics 4 ID')}
              </label>
              <input
                type="text"
                id="ga4-id"
                value={ga4Id}
                onChange={(e) => setGa4Id(e.target.value)}
                placeholder={t('bioPageBuilder.tracking.ga4Placeholder', 'e.g., G-XXXXXXXXXX')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Publish Section */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {t('bioPageBuilder.publish.title', 'Publish')}
            </h2>

            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">
                  {t('bioPageBuilder.publish.status', 'Publish Status')}
                </p>
                <p className="text-xs text-gray-500">
                  {isPublished
                    ? t('bioPageBuilder.publish.statusPublished', 'Your bio page is live and visible to everyone')
                    : t('bioPageBuilder.publish.statusDraft', 'Your bio page is not visible to the public')
                  }
                </p>
              </div>
              <button
                role="switch"
                aria-checked={isPublished}
                aria-label={t('bioPageBuilder.publish.toggleLabel', 'Publish bio page')}
                onClick={handleTogglePublish}
                disabled={publishing || !title}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                  isPublished ? 'bg-primary-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    isPublished ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {isFreePlan && !isPublished && (
              <p className="text-xs text-amber-600">
                {t('bioPageBuilder.publish.starterRequired', 'Publishing requires a Starter plan or higher.')}
              </p>
            )}

            {isPublished && bioPageUrl && (
              <div className="mt-4 p-3 bg-green-50 rounded-lg">
                <p className="text-sm font-medium text-green-800 mb-2">
                  {t('bioPageBuilder.publish.liveUrl', 'Your bio page is live at:')}
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={bioPageUrl}
                    className="flex-1 px-3 py-2 bg-white border border-green-200 rounded-lg text-sm text-gray-700"
                  />
                  <button
                    onClick={handleCopyUrl}
                    className="px-3 py-2 bg-white border border-green-200 rounded-lg text-sm text-green-700 hover:bg-green-100 transition-colors"
                  >
                    {copied ? t('bioPageBuilder.publish.copied', 'Copied!') : t('bioPageBuilder.publish.copy', 'Copy')}
                  </button>
                  <a
                    href={bioPageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition-colors"
                  >
                    {t('bioPageBuilder.publish.preview', 'Preview')}
                  </a>
                </div>
              </div>
            )}

            {!isPublished && bioPageUrl && (
              <p className="text-xs text-gray-500">
                {t('bioPageBuilder.publish.willBeAt', 'When published, your page will be available at')}: {bioPageUrl}
              </p>
            )}
          </div>
        </div>

        {/* Preview Panel */}
        <div className="lg:sticky lg:top-4 h-fit">
          <div className="bg-gray-100 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 text-center">
              {t('bioPageBuilder.previewButton', 'Preview')}
            </h2>
            <BioPagePreview
              title={title || t('bioPageBuilder.defaultTitle', 'Your Shop')}
              description={description}
              theme={theme}
              colors={colors}
              products={selectedProducts}
              logoUrl={logoUrl}
              shopUrl={shopUrl}
              impressumUrl={impressumUrl}
              datenschutzUrl={datenschutzUrl}
            />
          </div>
        </div>
      </div>

      {/* Upgrade Modal for Free Plan */}
      {showUpgradeModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="upgrade-modal-title"
          onKeyDown={(e) => { if (e.key === 'Escape') setShowUpgradeModal(false) }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowUpgradeModal(false)
          }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 id="upgrade-modal-title" className="text-lg font-semibold text-gray-900 mb-2">
              {t('bioPageBuilder.upgrade.title', 'Starter Plan Required')}
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              {t('bioPageBuilder.upgrade.message', 'Publishing your Bio Page requires a Starter plan or higher. Upgrade now to make your page visible to the public.')}
            </p>
            <div className="mb-4 p-3 bg-primary-50 rounded-lg text-sm text-gray-700 space-y-1">
              <p className="font-medium text-primary-700">
                {t('bioPageBuilder.upgrade.price', 'From €9/month')}
              </p>
              <p>✓ {t('bioPageBuilder.upgrade.benefit1', 'Your own Bio Page')}</p>
              <p>✓ {t('bioPageBuilder.upgrade.benefit2', 'Up to 50 products')}</p>
              <p>✓ {t('bioPageBuilder.upgrade.benefit3', '100 stories/month')}</p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowUpgradeModal(false)}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('bioPageBuilder.upgrade.cancel', 'Cancel')}
              </button>
              <Link
                to="/subscription"
                className="px-4 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
              >
                {t('bioPageBuilder.upgrade.button', 'Upgrade Plan')}
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BioPageBuilder
